import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter, map, switchMap, take, takeUntil, tap, timer } from 'rxjs';
import { AsyncTaskAction, AsyncTaskAvailableActions, AsyncTaskOptionStep, AsyncTaskStates, AsyncTaskWizardSteps, AsyncTaskOutputSummary } from './async-task.interface';
import { AsyncTaskWizard } from './async-task-wizard';
import { AsyncTasksWizardStore } from './async-tasks-wizard.store';
import { EasFormGroup, EasFormService } from '@fry/components/common/easform';
import { BuilderFormService } from '@fry/components/common/form-builder/form.service';
import { SearchModel, TableRequest } from '@fry/lib/list';

export interface WizardStep {
  id: AsyncTaskWizardSteps,
  label: string,
  index: number,
  completed: boolean,
  editable: boolean,
  skipped?: boolean
}

const STEPS = [
  {
    id: AsyncTaskWizardSteps.STEP_1,
    label: $localize `Select bookings`,
    link: ['step1'],
    index: 1,
  },
  {
    id: AsyncTaskWizardSteps.STEP_2,
    label: $localize `Review selection`,
    link: ['step2'],
    index: 2,
  },
  {
    id: AsyncTaskWizardSteps.STEP_3,
    label: $localize `Select bulk action`,
    link: ['step3'],
    index: 3,
  },
  {
    id: AsyncTaskWizardSteps.STEP_4,
    label: $localize `Bulk action options`,
    link: ['step4'],
    index: 4,
  },
  {
    id: AsyncTaskWizardSteps.STEP_5,
    label: $localize `Confirmation`,
    link: ['step5'],
    index: 5,
  },
  {
    id: AsyncTaskWizardSteps.STEP_6,
    label: $localize `Summary`,
    link: ['step6'],
    index: 6,
  },
]

@Injectable()
export class AsyncTaskWizardService {

  private finished$ = new Subject<void>();
  private _asyncTask$ = new BehaviorSubject<AsyncTaskWizard | null>(null);
  public asyncTask$ = this._asyncTask$.asObservable();
  public steps: WizardStep[];

  private currentSearchModel: SearchModel | null = null;

  constructor(
    private asyncTasksStore: AsyncTasksWizardStore,
    private easform: EasFormService,
    private formService: BuilderFormService,
  ) {
    this.steps = STEPS.map(itm => {
      return {
        ...itm,
        completed: false,
        editable: false,
      }
    });

    this.asyncTask$.subscribe(asyncTask => {
      if (asyncTask !== null) {
        this.updateSteps(asyncTask)
      }
    });
  }

  getTask(): Observable<AsyncTaskWizard> {
    return this.asyncTask$.pipe(
      filter(asyncTask => asyncTask !== null),
      map(asyncTask => {
        // This is redundant but typescript does not understand the filter
        // does not guarantee it is not null
        if (asyncTask === null) {
          throw new Error("Rxjs filter failed");
        }
        return asyncTask
      }),
      take(1)
    )
  }

  summary(): Observable<AsyncTaskOutputSummary> {
    let asyncTaskId: string;

    return this.getTask().pipe(
      switchMap(asyncTask => {
        asyncTaskId = asyncTask.id;
        return timer(0, 2000);
      }),
      takeUntil(this.finished$),
      switchMap(() => {
        return this.asyncTasksStore.summary(asyncTaskId)
      }),
      tap(summary => {
        if (![AsyncTaskStates.DRAFT, AsyncTaskStates.NEW, AsyncTaskStates.PROCESSING].includes(summary.state)) {
          this.finished$.next();
        }
      })
    )
  }

  updateSearch(search: SearchModel) {
    this.currentSearchModel = search;
    return this.getTask().pipe(
      map(asyncTask => {

        const originalSearch: SearchModel = asyncTask.doc.data.search || { filter: {}, sort: {} };
        console.log(originalSearch.select, search.select);
        if (search.select && search.select.length > 0) {
          originalSearch.select = search.select;
        }
        if (search.sort && Object.keys(search).sort.length > 0) {
          originalSearch.sort = search.sort;
        }
      })
    )
  }

  getCurrentSortSelect() {
    const currentSortSelect: { sort?: {}, select?: string[] } = {};
    if (this.currentSearchModel?.sort && Object.keys(this.currentSearchModel.sort).length > 0) {
      currentSortSelect.sort = this.currentSearchModel.sort;
    }

    if (this.currentSearchModel?.select && this.currentSearchModel.select.length > 0) {
      currentSortSelect.select = this.currentSearchModel.select;
    }

    return currentSortSelect;
  }

  set(asyncTaskWizard: AsyncTaskWizard | null) {
    this._asyncTask$.next(asyncTaskWizard);
  }

  private updateSteps(asyncTask: AsyncTaskWizard) {
    const step = asyncTask.wizardData.currentStep;
    const state = asyncTask.doc.state;
    const actionSteps = asyncTask.doc.wizardHelpers?.steps || [];

    const selectedStep = this.steps.find(itm => itm.id === step)
    if (selectedStep === undefined) {
      this.steps.forEach(itm => {
        itm.completed = false;
        itm.editable = false;
      })
    } else if (![AsyncTaskStates.DRAFT, AsyncTaskStates.PREPARING].includes(state)) {
      this.steps.forEach(itm => {
        itm.editable = itm.id == AsyncTaskWizardSteps.STEP_6;
        itm.completed = true;
      })
    } else {
      this.steps.forEach(itm => {
        if (itm.id === AsyncTaskWizardSteps.STEP_4) {
          itm.skipped = actionSteps.length === 0;
        }
        itm.editable = itm.index <= selectedStep.index;
        itm.completed = itm.index < selectedStep.index;
      })
    }
  }

  validStep(step: AsyncTaskWizardSteps) {
    const currentStep = this.steps.find(itm => itm.id === step);
    if (currentStep === undefined) {
      return false;
    }
    return currentStep.editable;
  }

  updateSelection(source: any, search: SearchModel) {
    /*  Update selection and search from step 1 */
    const data = {
      wizardData: {
        currentStep: AsyncTaskWizardSteps.STEP_2,
      },
      data: {
        source: source,
        search: search
      }
    }

    // doMagic
    return this.saveAndUpdateStep(data);
  }

  getMergedCurrentSortSelect() {
    return this.getTask().pipe(
      map(asyncTask => {
        const originalSearch: SearchModel = asyncTask.doc.data.search || { filter: {}, sort: {} };
        const currentSortSelect = this.getCurrentSortSelect();
        if (currentSortSelect.sort !== undefined) {
          originalSearch.sort = currentSortSelect.sort;
        }
        if (currentSortSelect.select !== undefined) {
          originalSearch.select = currentSortSelect.select;
        }
        return originalSearch;
      })
    )
  }

  confirmSelection() {
    /* Confirm selected items from step 2 */
    return this.getMergedCurrentSortSelect().pipe(
      switchMap(search => {
        const data = {
          wizardData: {
            currentStep: AsyncTaskWizardSteps.STEP_3,
          },
          data: {
            search: search
          }
        }
        return this.saveAndUpdateStep(data);
      })
    )
  }

  selectAction(action: AsyncTaskAction) {
    const data = {
      wizardData: {
        selectedAction: action.id,
        currentStep: AsyncTaskWizardSteps.STEP_4,
      }
    }
    return this.saveAndUpdateStep(data);
  }

  updateData(data: any, notFull: boolean) {
    /* Update data from step 4 */
    const step = notFull ? AsyncTaskWizardSteps.STEP_4 : AsyncTaskWizardSteps.STEP_5;
    const payload = {
      wizardData: {
        currentStep: step,
      },
      data: {
        params: {
          data: data
        }
      }
    }
    return this.saveAndUpdateStep(payload);
  }

  confirm() {
    /* Confirm action from step 5 */
    let asyncTaskId: string;
    return this.getTask().pipe(
      switchMap(asyncTask => {
        asyncTaskId = asyncTask.id;
        return this.getMergedCurrentSortSelect();
      }),
      switchMap(search => {
        const data = {
          wizardData: {
            currentStep: AsyncTaskWizardSteps.STEP_6,
          },
          data: {
            search: search
          }
        }
        return this.saveAndUpdateStep(data);
      }),
      switchMap(() => this.asyncTasksStore.trigger(asyncTaskId)),
      switchMap(() => this.asyncTasksStore.get(asyncTaskId)),
      tap(model => this.set(model))
    )
  }

  retryFailed() {
    return this.getTask().pipe(
      switchMap(asyncTask => {
        return this.asyncTasksStore.rerun(asyncTask.id, { failedOnly: true });
      })
    )
  }

  abort() {
    return this.getTask().pipe(
      switchMap(asyncTask => {
        return this.asyncTasksStore.abort(asyncTask.id);
      })
    )
  }

  availableActions$(): Observable<AsyncTaskAvailableActions> {
    return this.getTask().pipe(
      switchMap(asyncTask => {
        return this.asyncTasksStore.getAvailableActions(asyncTask.id);
      })
    )
  }

  loadSteps$(): Observable<AsyncTaskOptionStep[]> {
    return this.getTask().pipe(
      switchMap(asyncTask => {
        if (asyncTask.wizardData.selectedAction === undefined) {
          throw new Error('Please select an action first');
        }

        return this.asyncTasksStore.getActionSteps(asyncTask.id);

      })
    )
  }

  actionForm$(step: AsyncTaskOptionStep, initial?: any): Observable<EasFormGroup> {
    return this.getTask().pipe(
      switchMap(asyncTask => {
        if (asyncTask.wizardData.selectedAction === undefined) {
          throw new Error('Please select an action first');
        }

        if (initial === undefined) {
          initial = asyncTask.doc.data.params.data;
        }

        const data = {
          step: step.id,
          data: asyncTask.doc.data.params.data,
        };
        return this.asyncTasksStore.getActionForm(asyncTask.id, data).pipe(
          switchMap(stepForm => {
            return this.formService.buildForm(stepForm.form, { user: '__bulk__' }).pipe(
              map(group => {
                initial = {
                  ...stepForm.initial,
                  ...initial
                };
                const form = this.easform.createForm(group, initial || {});
                return form;
              })
            );
          })
        );
      })
    )
  }

  actionDetails$() {
    return this.getTask().pipe(
      switchMap(asyncTask => {
        if (asyncTask.wizardData.selectedAction === undefined) {
          throw new Error('Please select an action first');
        }
        return this.asyncTasksStore.getActionDetails(asyncTask.id)
      })
    )
  }

  private saveAndUpdateStep(data: any): Observable<AsyncTaskWizard> {
    return this.getTask().pipe(
      switchMap(asyncTask => {
        return this.asyncTasksStore.update(asyncTask.id, data).pipe(
          map(model => {
            this.set(model)
            return model;
          })
        )
      })
    )
  }

  public selectTableSource() {
    if (this._asyncTask$.value === null) {
      throw new Error("Task not properly initialised");
    }

    return this.asyncTasksStore.selectTableSource(this._asyncTask$.value.id);
  }

  public itemsTableSource() {
    if (this._asyncTask$.value === null) {
      throw new Error("Task not properly initialised");
    }

    return this.asyncTasksStore.itemsTableSource(this._asyncTask$.value.id);
  }

  public confirmTableSource() {
    if (this._asyncTask$.value === null) {
      throw new Error("Task not properly initialised");
    }

    return this.asyncTasksStore.confirmTableSource(this._asyncTask$.value.id);
  }

  public outputTableSource() {
    if (this._asyncTask$.value === null) {
      throw new Error("Task not properly initialised");
    }

    return this.asyncTasksStore.outputTableSource(this._asyncTask$.value.id);
  }

  public getItemCount(): Observable<number | undefined> {
    const source = this.itemsTableSource();
    const request: TableRequest = {
      filter: {},
      sort: {},
      pagination: {
        size: 0,
        start: 0
      },
      includeParts: ['data']
    }
    return source.table(request).pipe(
      map(res => {
        return res.meta?.total;
      })
    )
  }

  public cancelTask() {
    return this.getTask().pipe(
      switchMap(asyncTask => {
        return this.asyncTasksStore.remove(asyncTask.id);
      })
    )
  }

  public preserveTask() {
    const data = {
      state: 'preparing'
    }
    return this.saveAndUpdateStep(data)
  }

}
